import { Alert, Collapse, Skeleton, Stack, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { Dialog } from "../../components/dialogs/Dialog";
import { StandardLibraryTable } from "../../components/table/StandardLibraryTable";
import { useAppQuery } from "../../hooks/useAppQuery";
import { ITaskDetail } from "../../interfaces/Task";
import { useLocale } from "../../providers/LocaleProvider";
import CancelIcon from "../../resources/images/icons-close.svg";
import InfoIcon from "../../resources/images/icons-info-circle.svg";
import { KLAYO_COLORS } from "../../themes/colors";

const ImportTaskDetailDialog = (props: any) => {
  const {
    theme,
    onClose,
    open = false,
    taskId,
    isImport = true,
    onImport = () => {},
    isImporting = false,
    showWarning = false
  } = props;
  const { translate } = useLocale();
  const [openWarning, setOpenWarning] = React.useState(true);
  const { data: taskData, isLoading: isLoadingTask } = useAppQuery<ITaskDetail>(
    `/StandardLibrary/tasks/${taskId}`,
    {
      enabled: !!taskId
    }
  );

  const task = useMemo(() => taskData?.standardLibraryTask, [taskData?.standardLibraryTask]);
  const hasLimitedAccess = useMemo(
    () => taskData?.standardLibraryTask?.hasLimitedAccess,
    [taskData?.standardLibraryTask?.hasLimitedAccess]
  );

  return (
    <Dialog
      open={open}
      theme={theme}
      onClose={onClose}
      fullWidth={true}
      maxWidth='md'
      title={translate("settings.taskDetails")}
      actions={[
        ...(isImport
          ? [
              {
                label: translate("settings.importTask"),
                primary: true,
                variant: "filled",
                disabled: isLoadingTask || isImporting,
                onClick: () => onImport(taskId),
                isLoading: isImporting
              }
            ]
          : []),
        {
          label: translate("common.cancel"),
          onClick: onClose
        }
      ]}
    >
      <Stack spacing={2}>
        <div>
          <Typography variant='subtitle2' color='text.secondary'>
            {translate("settings.taskName")}
          </Typography>
          {isLoadingTask ? (
            <Skeleton variant='text' width='60%' />
          ) : (
            <Typography>{task?.name}</Typography>
          )}
        </div>
        <>
          <div hidden={!task?.taskCompetencies.length && !isLoadingTask}>
            {isLoadingTask ? (
              <>
                <Skeleton variant='text' width='40%' />
                <Skeleton variant='text' width='50%' />
              </>
            ) : (
              <StandardLibraryTable
                theme={theme}
                columns={[
                  {
                    type: StandardLibraryTable.columns.name,
                    label: translate("entities.attribute")
                  },
                  {
                    type: StandardLibraryTable.columns.category,
                    field: "competencyCategories",
                    label: translate("tableColumns.category")
                  },
                  {
                    type: StandardLibraryTable.columns.expiry,
                    label: translate("tableColumns.expiry")
                  },
                  {
                    type: StandardLibraryTable.columns.training,
                    label: translate("tableColumns.training")
                  }
                ]}
                paginationMode='server'
                rows={(task?.taskCompetencies || []) as any}
                hideFirstLastBorder={false}
                dense={true}
                paper={false}
                selectable={false}
                sx={{ "& .MuiDataGrid-row": { cursor: "pointer !important" } }}
                showFooter={false}
              />
            )}
          </div>
          {hasLimitedAccess && (
            <Typography textAlign={"center"}>{translate("settings.contactToUpgrade")}</Typography>
          )}
          {showWarning && (
            <Collapse in={openWarning}>
              <Alert
                icon={<InfoIcon />}
                severity='info'
                action={
                  <CancelIcon
                    onClick={() => {
                      setOpenWarning(false);
                    }}
                    className='klayo__alert-close-icon'
                    style={{ cursor: "pointer", padding: "0 0.575rem 0 0", scale: "0.75" }}
                  />
                }
                onClose={() => {}}
                sx={{
                  alignItems: "center",
                  fontWeight: 500,
                  borderRadius: "2rem",
                  color: KLAYO_COLORS.GreyGR900,
                  fontSize: ".875rem"
                }}
              >
                {translate("settings.linkedCompetenciesImported")}
              </Alert>
            </Collapse>
          )}
        </>
      </Stack>
    </Dialog>
  );
};

export default ImportTaskDetailDialog;
